import {
    collection,
    doc,
    getCountFromServer,
    getDoc,
    getDocs,
    limit,
    query,
    serverTimestamp,
    where,
    writeBatch,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';

export const normalizeFollowPublicKey = (value = '') => String(value || '').trim();

export const buildFollowDocId = (value = '') => normalizeFollowPublicKey(value);

const chunkArray = (items, size) => {
    const chunks = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
};

export const getFollowerCount = async (targetPublicKey) => {
    const target = normalizeFollowPublicKey(targetPublicKey);
    if (!target) return 0;

    const snap = await getCountFromServer(collection(db, 'users', target, 'followers'));
    return Number(snap.data().count || 0);
};

export const getIsFollowing = async ({ followerPublicKey, targetPublicKey }) => {
    const follower = normalizeFollowPublicKey(followerPublicKey);
    const target = normalizeFollowPublicKey(targetPublicKey);
    if (!follower || !target || follower === target) return false;

    const snap = await getDoc(doc(db, 'users', follower, 'following', target));
    return snap.exists();
};

export const followSpace = async ({
                                      followerPublicKey,
                                      targetPublicKey,
                                      followerMuultiName = '',
                                      targetMuultiName = '',
                                  }) => {
    const follower = normalizeFollowPublicKey(followerPublicKey);
    const target = normalizeFollowPublicKey(targetPublicKey);
    if (!follower) throw new Error('Follower public key is required.');
    if (!target) throw new Error('Target public key is required.');
    if (follower === target) throw new Error('You cannot follow your own space.');

    const targetSnap = await getDoc(doc(db, 'users', target));

    const now = serverTimestamp();
    const followerName = String(followerMuultiName || '').trim();
    const targetName = String(targetMuultiName || targetSnap.data()?.muultiName || '').trim();
    const batch = writeBatch(db);

    batch.set(doc(db, 'users', target, 'followers', follower), {
        followerPublicKey: follower,
        followerMuultiName: followerName,
        targetPublicKey: target,
        targetMuultiName: targetName,
        createdAt: now,
        updatedAt: now,
    });

    batch.set(doc(db, 'users', follower, 'following', target), {
        followerPublicKey: follower,
        followerMuultiName: followerName,
        targetPublicKey: target,
        targetMuultiName: targetName,
        createdAt: now,
        updatedAt: now,
    });

    await batch.commit();
};

export const unfollowSpace = async ({ followerPublicKey, targetPublicKey }) => {
    const follower = normalizeFollowPublicKey(followerPublicKey);
    const target = normalizeFollowPublicKey(targetPublicKey);
    if (!follower || !target) return;

    const batch = writeBatch(db);
    batch.delete(doc(db, 'users', target, 'followers', follower));
    batch.delete(doc(db, 'users', follower, 'following', target));
    await batch.commit();
};

export const loadFollowingPublicKeys = async (followerPublicKey) => {
    const follower = normalizeFollowPublicKey(followerPublicKey);
    if (!follower) return [];

    const snap = await getDocs(collection(db, 'users', follower, 'following'));
    const seen = new Set();
    const keys = [];
    snap.forEach((docSnap) => {
        const data = docSnap.data() || {};
        const target = normalizeFollowPublicKey(data.targetPublicKey || docSnap.id);
        if (!target || seen.has(target)) return;
        seen.add(target);
        keys.push(target);
    });
    return keys;
};

export const loadFollowers = async (targetPublicKey, maxFollowers = 500) => {
    const target = normalizeFollowPublicKey(targetPublicKey);
    if (!target) return [];

    const snap = await getDocs(query(
        collection(db, 'users', target, 'followers'),
        limit(maxFollowers)
    ));
    return snap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...(docSnap.data() || {}),
        followerPublicKey: normalizeFollowPublicKey(docSnap.data()?.followerPublicKey || docSnap.id),
    }));
};

export const loadSpaceProfilesByPublicKey = async (publicKeys = []) => {
    const normalizedKeys = Array.from(new Set(
        (Array.isArray(publicKeys) ? publicKeys : [])
            .map((key) => normalizeFollowPublicKey(key))
            .filter(Boolean)
    ));
    if (!normalizedKeys.length) return new Map();

    const profiles = new Map();
    const chunks = chunkArray(normalizedKeys, 10);
    for (const chunk of chunks) {
        const snap = await getDocs(query(
            collection(db, 'users'),
            where('publicKey', 'in', chunk)
        ));
        snap.forEach((docSnap) => {
            const data = docSnap.data() || {};
            const publicKey = normalizeFollowPublicKey(data.publicKey || docSnap.id);
            if (!publicKey) return;
            profiles.set(publicKey, { id: docSnap.id, ...data, publicKey });
        });
    }

    return profiles;
};
